import type { PromoPeriodModel } from '@/shared/generated'
import type { JSONSchemaType } from 'ajv'
import type { DateRange } from 'react-day-picker'

import { useMemo } from 'react'
import { addDays, format, startOfDay } from 'date-fns'
import { pipe, prop } from 'lodash/fp'
import {
	Controller,
	useController,
	useFormContext,
	useWatch,
} from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { useSettingsSchemasQuery } from '@/entities/core'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	DatePicker,
	DatePickerCalendar,
	DatePickerContent,
	DatePickerInput,
} from '@/shared/components/ui/date-picker'
import {
	Field,
	FieldDescription,
	FieldError,
} from '@/shared/components/ui/field'
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from '@/shared/components/ui/input-group'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import { useAppStore } from '@/shared/contexts/app'
import { DateFormat, utcToZonedTime } from '@/shared/tools/dates'
import { getResolvedSchema } from '@/shared/tools/json-schema-utils'
import { asNumber } from '@/shared/tools/utils'

const MAX_PROMO_PERIOD_RANGE = 13
const DEFAULT_PERIOD_RANGE = 6

const getDefaultPromoPricingTactics = () => {
	const currentDate = new Date()

	return {
		promo_start_date: currentDate,
		promo_end_date: addDays(currentDate, DEFAULT_PERIOD_RANGE),
		target: {
			name: 'sales_items_uplift',
			value: 0,
			measure: '%',
		},
	}
}
export function PromoPeriodFieldController({ name }: { name: string }) {
	const { t } = useTranslation()

	return (
		<ErrorBoundary>
			<div className='space-y-2'>
				<h5>
					{t('app.promo_period.title', {
						defaultValue: 'Promo period',
					})}
				</h5>
				<div className='flex gap-2'>
					<PromoPeriodRangeController name={name} />
					<PromoTargetController name={name} />
				</div>
			</div>
		</ErrorBoundary>
	)
}
const toDate = (value: string | Date) =>
	startOfDay(utcToZonedTime(new Date(value), 'UTC'))
const PromoPeriodRangeController = ({ name }: { name: string }) => {
	const defaults = useMemo(() => getDefaultPromoPricingTactics(), [])
	const dateFormat = useAppStore((state) => state.dateFormat)
	const { control, clearErrors } = useFormContext()
	const startDateField = useController({
		control,
		name: `${name}.promo_start_date`,
	})
	const endDateField = useController({
		control,
		name: `${name}.promo_end_date`,
	})
	const start = startDateField.field.value
	const end = endDateField.field.value

	const handleChange = ({ from, to }: DateRange) => {
		if (from) {
			startDateField.field.onChange(format(from, DateFormat.system))
		}
		if (to) {
			endDateField.field.onChange(format(to, DateFormat.system))
		}
		clearErrors([`${name}.promo_start_date`, `${name}.promo_end_date`])
	}

	return (
		<Field className='mb-0'>
			<DatePicker
				required
				mode='range'
				value={{
					from: toDate(start || defaults.promo_start_date),
					to: toDate(end || defaults.promo_end_date),
				}}
				onValueChange={handleChange}
			>
				<DatePickerInput format={dateFormat} />
				<DatePickerContent>
					<DatePickerCalendar />
				</DatePickerContent>
			</DatePicker>
			<FieldDescription>
				<Trans
					i18nKey='app.promo_period.description'
					defaults='The maximum period is <strong>{max}</strong> days.'
					values={{ max: MAX_PROMO_PERIOD_RANGE + 1 }}
					components={{ strong: <strong className='text-foreground' /> }}
				/>
			</FieldDescription>
			{startDateField.fieldState.invalid && (
				<FieldError errors={[startDateField.fieldState.error]} />
			)}
			{endDateField.fieldState.invalid && (
				<FieldError errors={[endDateField.fieldState.error]} />
			)}
		</Field>
	)
}

const PromoTargetController = ({ name }: { name: string }) => {
	const { t } = useTranslation()
	const { control } = useFormContext()
	const defaults = useMemo(() => getDefaultPromoPricingTactics(), [])
	const targetName = useWatch({ control, name: `${name}.target.name` })
	const promoTacticsPeriodsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:tactics_promo'),
			prop('properties.params.properties'),
		]),
		refetchOnWindowFocus: 'always',
	})
	const { measure, strategiesOptions } = useMemo(() => {
		const availableConstraints =
			promoTacticsPeriodsSchema?.data?.target?.oneOf ?? []
		const strategiesOptions = availableConstraints.map(
			(schemaChoice: JSONSchemaType<PromoPeriodModel>) => ({
				label: t(schemaChoice.title, {
					defaultValue: schemaChoice.title,
				}),
				value: schemaChoice.properties.name.const,
			})
		) as {
			label: string
			value: string
		}[]
		const measure =
			availableConstraints.find(
				(schemaChoice: JSONSchemaType<PromoPeriodModel>) =>
					targetName === schemaChoice.properties?.name?.const
			)?.properties?.value?.measure ?? defaults.target.measure

		return {
			measure,
			strategiesOptions,
		}
	}, [promoTacticsPeriodsSchema, targetName, defaults, t])

	return (
		<>
			<Controller
				name={`${name}.target.name`}
				render={({ field, fieldState }) => (
					<Field data-invalid={fieldState.invalid}>
						<Select
							name={field.name}
							value={field.value}
							onValueChange={field.onChange}
						>
							<SelectTrigger
								data-name='target-name'
								data-testid='target-name-select'
								aria-invalid={fieldState.invalid}
							>
								<SelectValue data-testid='target-name-select-value' />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									{strategiesOptions.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
						{fieldState.invalid && <FieldError errors={[fieldState.error]} />}
					</Field>
				)}
			/>
			<Controller
				name={`${name}.target.value`}
				render={({ field, fieldState }) => (
					<Field data-invalid={fieldState.invalid}>
						<InputGroup>
							<InputGroupInput
								value={field.value}
								onChange={(e) => {
									field.onChange(asNumber(e.target.value))
								}}
								type='number'
								name={name}
								aria-invalid={fieldState.invalid}
							/>
							<InputGroupAddon align='inline-end'>{measure}</InputGroupAddon>
						</InputGroup>
						{fieldState.invalid && <FieldError errors={[fieldState.error]} />}
					</Field>
				)}
			/>
		</>
	)
}
